/**
 * DietaryPills — et medlems allergier og kosthold som piller (#159, steg 9a).
 *
 * Allergier er sikkerhetskritiske (CLAUDE.md): de står synlig på barnekortene
 * på Familie og på medlemsradene i administrasjonen, ikke bare inne i
 * redigeringsskjemaet. Allergiene får amber varseltone (D3), kostholdet nøytral
 * tone, og hver pille starter med ordet «Allergi» eller «Kosthold», så fargen
 * aldri er eneste kjennetegn. Merknaden (f.eks. hvor alvorlig allergien er)
 * står under pillene. Ingen oppføringer gir ingenting, ikke en tom rad.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { MemberAdminDietary } from '@stell/shared';
import { Pill } from '../../components/primitives';
import { dietaryLabels } from '../shopping/useHouseholdDietary';

export function DietaryPills({
  dietary,
  className,
  'data-testid': testId,
}: {
  dietary: MemberAdminDietary;
  className?: string;
  'data-testid': string;
}): ReactNode {
  const { t } = useTranslation();
  const { allergies, dietary: diet } = dietaryLabels(dietary, t);
  const notes = dietary.notes?.trim();
  const hasPills = allergies.length > 0 || diet.length > 0;
  if (!hasPills && !notes) return null;
  return (
    <div className={['flex flex-col gap-1', className].filter(Boolean).join(' ')} data-testid={testId}>
      {hasPills ? (
        <p className="m-0 flex flex-wrap gap-1.5">
          {allergies.length > 0 ? (
            <Pill variant="warn">{t('admin.members.allergies', { list: allergies.join(', ') })}</Pill>
          ) : null}
          {diet.length > 0 ? (
            <Pill variant="neutral">{t('admin.members.dietary', { list: diet.join(', ') })}</Pill>
          ) : null}
        </p>
      ) : null}
      {notes ? <p className="m-0 text-caption text-ink-2">{t('admin.members.dietaryNotes', { notes })}</p> : null}
    </div>
  );
}
